import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Check, Clock } from 'lucide-react';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import { formatAgo, formatDateTime, pluralise } from '@/shared/lib/format';
import type { DeskQuestion } from '../api/deskApi';
import { DeskAnswer } from './DeskAnswer';
import { DeskReply } from './DeskReply';
import { channelName } from './DeskList';

type Props = { q: DeskQuestion; canAnswer: boolean; role: string; onAnswered: () => void };

function Header({ q }: { q: DeskQuestion }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
      <PersonAvatar name={q.askedBy} size={32} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{q.askedBy}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Asked via {channelName(q)} · {formatDateTime(q.createdAt)}</Typography>
      </Box>
      {q.status === 'answered' ? (
        <Stack direction="row" sx={answeredSx}><Check size={12} aria-hidden />Answered</Stack>
      ) : null}
      <Box component="span" sx={idSx}>{q.id}</Box>
    </Stack>
  );
}

function Thread({ context }: { context: string[] }) {
  if (context.length === 0) return null;
  return (
    <Box component="section" aria-labelledby="desk-thread" sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <Typography id="desk-thread" variant="caption" component="h3" sx={{ fontWeight: 600 }}>Earlier in the conversation</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{pluralise(context.length, 'question')}</Typography>
      </Stack>
      <Stack component="ol" sx={{ m: 0, p: 0, listStyle: 'none', gap: 0.75 }}>
        {context.map((c, i) => (
          <Stack component="li" direction="row" key={`${i}-${c}`} sx={{ gap: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', width: 12, pt: 0.25 }}>{i + 1}</Typography>
            <Typography variant="body2">{c}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/** The selected question in full: who asked and where, the chat it came from, and the reply or a box to write it. */
export function DeskDetail({ q, canAnswer, role, onAnswered }: Props) {
  const isOpen = q.status === 'open';
  return (
    <Stack component="article" aria-labelledby="desk-question" sx={{ height: '100%' }}>
      <Stack key={q.id} sx={{ ...enterSx, gap: 2, p: { xs: 2, md: 3 }, flexGrow: 1 }}>
        <Header q={q} />
        <Typography id="desk-question" variant="h2" component="h2" sx={{ lineHeight: 1.35 }}>{q.question}</Typography>
        {isOpen ? (
          <Stack direction="row" sx={noticeSx}>
            <Clock size={14} aria-hidden style={{ flexShrink: 0 }} />
            The assistant found no matching research, so this was sent to the desk {formatAgo(q.createdAt)}.
          </Stack>
        ) : null}
        <Thread context={q.context} />
        <DeskAnswer q={q} />
      </Stack>
      {isOpen ? (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <DeskReply key={q.id} q={q} canAnswer={canAnswer} role={role} onAnswered={onAnswered} />
        </Box>
      ) : null}
    </Stack>
  );
}

const enterSx = {
  '@keyframes detailIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'detailIn 220ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

const noticeSx = {
  alignItems: 'center',
  alignSelf: 'flex-start',
  gap: 0.75,
  px: 1.25,
  py: 0.75,
  borderRadius: 1,
  bgcolor: 'surface.highlight',
  color: 'warning.main',
  fontSize: '0.75rem',
} as const;

const answeredSx = {
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: 5,
  bgcolor: 'color-mix(in srgb, var(--mui-palette-success-main) 12%, transparent)',
  color: 'success.dark',
  fontSize: '0.6875rem',
  fontWeight: 600,
} as const;

const idSx = {
  px: 0.75,
  py: 0.25,
  borderRadius: 0.75,
  border: 1,
  borderColor: 'divider',
  fontSize: '0.6875rem',
  color: 'text.secondary',
  fontWeight: 500,
} as const;
